import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Button, Form, Modal, OverlayTrigger } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ScaleLoader } from "react-spinners";
import { FcFinePrint } from "react-icons/fc";
import { FiChevronDown, FiRotateCcw } from "react-icons/fi";
import moment from "moment";
import { toast } from "react-toastify";
import { RegulationContext } from "../../../App";
import { getAnomalyRuleDescriptionKey, getAnomalyRuleLabelKey, resolveAnomalyRuleLabel, RULE_CODE } from "../../../core/constants/anomalyRuleCodes";
import AnomalyService from "../../../core/service/AnomalyService";
import { useAnomalyDetection } from "./AnomalyDetectionContext";
import { resolveAnomalyRegulationId } from "./anomalyRegulationUtils";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import Utils from "../../../core/utilities/Utils";
import AnomalyInfoTip from "./AnomalyInfoTip";
import {
  formatAnomalyDisplayDateTime,
  getAnomalyDefaultDetectMonth,
  getAnomalyMaxSelectableDate,
  onAnomalyPeriodChange,
} from "./anomalyDateUtils";
import "./AnomalyRuleConfig.scss";

const defaultDetectMonth = getAnomalyDefaultDetectMonth;

const toPeriodParam = (monthDate) =>
  moment(monthDate).startOf("month").format("YYYY-MM-DD");

const formatDetectMonthLabel = (monthDate) =>
  moment(monthDate).format("MMM YYYY");

const syncDraftFromRule = (rule) => ({
  threshold: rule?.thresholdPct ?? "",
  minPeriods: rule?.minConsecutivePeriods ?? "",
});

const ruleHasEditableParams = (rule) =>
  rule?.defaultThresholdPct != null
  || rule?.ruleCode === RULE_CODE.CONSECUTIVE_IDENTICAL;

const getRuleParamSummary = (rule) => {
  const summary = [];
  if (rule?.thresholdPct != null) summary.push(`${rule.thresholdPct}%`);
  if (rule?.minConsecutivePeriods != null) summary.push(`${rule.minConsecutivePeriods}`);
  return summary.join(" · ");
};

const AnomalyRuleConfig = ({ t }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { glbRegulation } = useContext(RegulationContext);
  const regulationId = resolveAnomalyRegulationId(glbRegulation);

  const { startDetect, isJobActive, lastRun } = useAnomalyDetection();
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [listFilter, setListFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [savingCode, setSavingCode] = useState(null);
  const [savePulseCode, setSavePulseCode] = useState(null);
  const [togglingCode, setTogglingCode] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState({ show: false, rule: null, enabled: false });
  const [showDetectModal, setShowDetectModal] = useState(false);
  const [detectMonth, setDetectMonth] = useState(defaultDetectMonth);
  const [detectError, setDetectError] = useState("");
  const [isStartingDetect, setIsStartingDetect] = useState(false);

  const fetchRules = () => {
    setIsLoading(true);
    AnomalyService.getRuleConfigs()
      .then((res) => setRules(res.data || []))
      .catch(() => { })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const sortedRules = useMemo(() => [...rules].sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return a.ruleCode.localeCompare(b.ruleCode);
  }), [rules]);

  const filteredRules = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sortedRules.filter((rule) => {
      if (listFilter === "active" && !rule.enabled) return false;
      if (listFilter === "inactive" && rule.enabled) return false;
      if (!q) return true;
      return (
        rule.ruleCode.toLowerCase().includes(q)
        || resolveAnomalyRuleLabel(t, rule.ruleCode).toLowerCase().includes(q)
      );
    });
  }, [sortedRules, listFilter, searchQuery, t]);

  const getRuleDraft = (rule) => {
    if (!rule) return { threshold: "", minPeriods: "" };
    return drafts[rule.ruleCode] ?? syncDraftFromRule(rule);
  };

  const setRuleDraft = (ruleCode, patch) => {
    setDrafts((prev) => {
      const rule = rules.find((r) => r.ruleCode === ruleCode);
      const base = prev[ruleCode] ?? syncDraftFromRule(rule);
      return { ...prev, [ruleCode]: { ...base, ...patch } };
    });
  };

  const isRuleDraftDirty = (rule) => {
    const draft = getRuleDraft(rule);
    return (
      String(draft.threshold) !== String(rule.thresholdPct ?? "")
      || String(draft.minPeriods) !== String(rule.minConsecutivePeriods ?? "")
    );
  };

  const toggleExpand = (rule) => {
    if (!ruleHasEditableParams(rule)) return;
    setExpandedCode((prev) => (prev === rule.ruleCode ? null : rule.ruleCode));
  };

  useEffect(() => {
    const focusRule = location.state?.focusRule;
    if (!focusRule || !rules.some((r) => r.ruleCode === focusRule)) return;

    const rule = rules.find((r) => r.ruleCode === focusRule);
    if (ruleHasEditableParams(rule)) {
      setExpandedCode(focusRule);
    }

    requestAnimationFrame(() => {
      const el = document.querySelector(`.rule-accordion-item[data-rule-code="${focusRule}"]`);
      if (el) {
        el.classList.add("rule-accordion-focus-pulse");
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        setTimeout(() => el.classList.remove("rule-accordion-focus-pulse"), 2400);
      }
    });
  }, [rules, location.state?.focusRule]);

  const enabledCount = rules.filter((r) => r.enabled).length;
  const coveragePct = rules.length > 0 ? Math.round((enabledCount / rules.length) * 100) : 0;

  const requestToggle = (rule, enabled) => {
    setConfirmToggle({ show: true, rule, enabled });
  };

  const handleToggleConfirm = (confirmed) => {
    const { rule, enabled } = confirmToggle;
    setConfirmToggle({ show: false, rule: null, enabled: false });
    if (!confirmed || !rule) return;

    setTogglingCode(rule.ruleCode);
    AnomalyService.toggleRuleConfig(rule.ruleCode, enabled)
      .then(() => {
        toast.success(t("AnomalyDetection.toggleSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchRules();
      })
      .catch(() => { })
      .finally(() => setTogglingCode(null));
  };

  const handleResetDraft = (rule) => {
    if (!rule) return;
    setRuleDraft(rule.ruleCode, syncDraftFromRule(rule));
  };

  const handleSave = (rule) => {
    if (!rule) return;
    const draft = getRuleDraft(rule);
    const payload = {
      ruleCode: rule.ruleCode,
      enabled: rule.enabled,
      thresholdPct: draft.threshold !== "" ? parseFloat(draft.threshold) : null,
      minConsecutivePeriods: draft.minPeriods !== "" ? parseInt(draft.minPeriods, 10) : null,
      customParam: rule.customParam ?? null,
    };

    setSavingCode(rule.ruleCode);
    AnomalyService.updateRuleConfig(payload)
      .then(() => {
        toast.success(t("AnomalyDetection.saveSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        setDrafts((prev) => ({
          ...prev,
          [rule.ruleCode]: {
            threshold: payload.thresholdPct ?? "",
            minPeriods: payload.minConsecutivePeriods ?? "",
          },
        }));
        setSavePulseCode(rule.ruleCode);
        setTimeout(() => setSavePulseCode(null), 1400);
        fetchRules();
      })
      .catch(() => { })
      .finally(() => setSavingCode(null));
  };

  const openDetectModal = () => {
    setDetectMonth(defaultDetectMonth());
    setDetectError("");
    setShowDetectModal(true);
  };

  const validateDetectForm = () => {
    if (!detectMonth) return t("AnomalyDetection.detectMonthRequired.label");
    if (!regulationId) return t("AnomalyDetection.detectNoRegulation.label");
    return "";
  };

  const isDetectBusy = isJobActive || isStartingDetect;

  const handleDetectSubmit = () => {
    const err = validateDetectForm();
    if (err) {
      setDetectError(err);
      return;
    }

    const period = toPeriodParam(detectMonth);
    const monthLabel = formatDetectMonthLabel(detectMonth);
    setDetectError("");
    setIsStartingDetect(true);

    startDetect(period, monthLabel)
      .then(() => {
        setShowDetectModal(false);
      })
      .catch((error) => {
        const apiMsg = error?.response?.data?.message;
        setDetectError(apiMsg || t("AnomalyDetection.detectError.label"));
      })
      .finally(() => setIsStartingDetect(false));
  };

  const renderRuleParams = (rule) => {
    const draft = getRuleDraft(rule);
    const isDirty = isRuleDraftDirty(rule);
    const isSaving = savingCode === rule.ruleCode;
    const saveDisabled = !isDirty || isSaving;
    const thresholdFillPct = Math.min(
      100,
      Math.max(0, Number(draft.threshold === "" ? 0 : draft.threshold) || 0)
    );

    return (
      <div className="rule-accordion-body">
        <h6 className="rule-accordion-section-title">
          {t("AnomalyRuleConfig.parametersSection.label")}
        </h6>

        {rule.defaultThresholdPct != null && (
          <Form.Group className="rule-param-field rule-param-field--threshold">
            <Form.Label className="rule-field-label label-with-tip">
              {t("AnomalyDetection.thresholdPct.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.thresholdHint.label")} />
            </Form.Label>
            <div className="threshold-input-wrap">
              <Form.Control
                type="range"
                min={0}
                max={100}
                step={1}
                value={draft.threshold === "" ? 0 : draft.threshold}
                onChange={(e) => setRuleDraft(rule.ruleCode, { threshold: e.target.value })}
                className="rule-threshold-slider"
                style={{ "--threshold-fill": `${thresholdFillPct}%` }}
              />
              <div className="threshold-number-wrap">
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={draft.threshold}
                  onChange={(e) => setRuleDraft(rule.ruleCode, { threshold: e.target.value })}
                />
                <span className="threshold-unit">%</span>
              </div>
            </div>
          </Form.Group>
        )}

        {rule.ruleCode === RULE_CODE.CONSECUTIVE_IDENTICAL && (
          <Form.Group className="rule-param-field rule-param-field--min-periods">
            <Form.Label className="rule-field-label label-with-tip">
              {t("AnomalyDetection.minPeriods.label")}
              <span className="field-optional">({t("AnomalyDetection.optional.label")})</span>
              <AnomalyInfoTip text={t("AnomalyDetection.minPeriodsHint.label")} />
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              step={1}
              value={draft.minPeriods}
              onChange={(e) => setRuleDraft(rule.ruleCode, { minPeriods: e.target.value })}
              placeholder="e.g. 2"
            />
          </Form.Group>
        )}

        <div className="rule-accordion-footer">
          {isDirty && (
            <span className="rule-draft-badge">{t("AnomalyRuleConfig.unsavedChanges.label")}</span>
          )}
          <div className="rule-accordion-actions">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleResetDraft(rule)}
              disabled={saveDisabled}
            >
              {t("AnomalyRuleConfig.resetChanges.label")}
            </Button>
            <Button
              variant="primary"
              className="sbmBtn"
              onClick={() => handleSave(rule)}
              disabled={saveDisabled}
            >
              {isSaving
                ? <ScaleLoader color="#fff" height={14} width={2} />
                : t("Save.label")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderRuleRow = (rule) => {
    const hasParams = ruleHasEditableParams(rule);
    const isExpanded = expandedCode === rule.ruleCode;
    const isToggling = togglingCode === rule.ruleCode;
    const paramSummary = getRuleParamSummary(rule);

    return (
      <li
        key={rule.ruleCode}
        data-rule-code={rule.ruleCode}
        className={`rule-accordion-item${rule.enabled ? " is-enabled" : " is-disabled"}${isExpanded ? " is-expanded" : ""}${savePulseCode === rule.ruleCode ? " is-saved" : ""}`}
      >
        <div className="rule-accordion-header">
          {hasParams ? (
            <button
              type="button"
              className="rule-accordion-expand"
              onClick={() => toggleExpand(rule)}
              aria-expanded={isExpanded}
              aria-label={resolveAnomalyRuleLabel(t, rule.ruleCode)}
            >
              <FiChevronDown className={`rule-accordion-chevron${isExpanded ? " is-open" : ""}`} />
            </button>
          ) : (
            <span className="rule-accordion-expand rule-accordion-expand--static" aria-hidden="true" />
          )}

          <button
            type="button"
            className={`rule-accordion-main${hasParams ? "" : " rule-accordion-main--static"}`}
            onClick={() => toggleExpand(rule)}
            disabled={!hasParams}
          >
            <span className="rule-accordion-indicator" aria-hidden="true" />
            <span className="rule-accordion-body-text">
              <span className="rule-accordion-body-text-inner">
                <span className="rule-accordion-name-row">
                  <span className="rule-accordion-name">{resolveAnomalyRuleLabel(t, rule.ruleCode)}</span>
                </span>
                <p>{t(getAnomalyRuleDescriptionKey(rule.ruleCode))}</p>
                {paramSummary && (
                  <span className="rule-accordion-params"> {t("AnomalyDetection.parameters.label")}: {paramSummary}</span>
                )}
              </span>
              <span className="rule-accordion-meta">
                <span className={`rule-accordion-status ${rule.enabled ? "is-on" : "is-off"}`}>
                  {rule.enabled ? t("AnomalyDetection.active.label") : t("AnomalyDetection.inactive.label")}
                </span>

              </span>
            </span>
          </button>

          <div className="rule-accordion-toggle">
            {isToggling ? (
              <ScaleLoader color="#3b82f6" height={14} width={2} margin={1} />
            ) : (
              <Form.Check
                type="switch"
                id={`rule-toggle-${rule.ruleCode}`}
                checked={!!rule.enabled}
                onChange={(e) => requestToggle(rule, e.target.checked)}
                label=""
              />
            )}
          </div>
        </div>

        {hasParams && isExpanded && renderRuleParams(rule)}
      </li>
    );
  };

  return (
    <div className="anomaly-ruleconfig-page" data-testid="anomaly-rule-config">
      <header className="ruleconfig-top-bar">
        <div className="ruleconfig-top-left">
          <h5 className="MH">
            <span
              className="back-nav-btn anomaly-back-nav-btn"
              onClick={() => navigate("../dashboard")}
              title={t("Back.label")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate("../dashboard")}
            >
              &#8592;
            </span>
            {t("AnomalyRuleConfig.label")}
          </h5>
          <p className="ruleconfig-subtitle">{t("AnomalyRuleConfig.subtitle.label")}</p>
        </div>

        <div className="ruleconfig-coverage-ring" aria-label={`${coveragePct}% ${t("AnomalyDetection.enabled.label")}`}>
          <svg viewBox="0 0 36 36" className="coverage-ring-svg">
            <path
              className="coverage-ring-bg"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              className="coverage-ring-fill"
              strokeDasharray={`${coveragePct}, 100`}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <span className="coverage-ring-label">
            <strong>{enabledCount}</strong>
            <small>/{rules.length}</small>
          </span>
        </div>

        <OverlayTrigger
          placement="top"
          overlay={Utils.renderTooltip(t("AnomalyDetection.detectToolTip.label"))}
        >
          <Button
            variant="outline-primary"
            size="sm"
            className="ruleconfig-detect-btn"
            onClick={openDetectModal}
            disabled={isDetectBusy}
          >
            <FiRotateCcw size={14} />
            {t("AnomalyDetection.runDetection.label")}
          </Button>
        </OverlayTrigger>
      </header>

      {lastRun && (
        <div className="ruleconfig-detect-banner">
          {t("AnomalyDetection.lastDetectTriggered.label", {
            month: lastRun.monthLabel,
            timestamp: formatAnomalyDisplayDateTime(lastRun.completedAt),
          })}
          {lastRun.totalRules != null && (
            <span className="ruleconfig-detect-banner-detail">
              {" · "}{t("AnomalyDetection.detectProgress.label", {
                processed: lastRun.processedRules,
                total: lastRun.totalRules,
              })}
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="ruleconfig-loading">
          <ScaleLoader color="#3b82f6" />
          <span>{t("AnomalyRuleConfig.loading.label")}</span>
        </div>
      ) : rules.length === 0 ? (
        <div className="ruleconfig-empty">
          <FcFinePrint size={40} />
          <p>{t("Nodatafound.label")}</p>
        </div>
      ) : (
        <div className="rule-accordion-panel">
          <div className="rule-accordion-controls">
            <Form.Control
              type="search"
              className="rule-accordion-search"
              placeholder={t("AnomalyRuleConfig.searchRules.label")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="rule-accordion-filters" role="tablist">
              {[
                { key: "all", label: t("AnomalyRuleConfig.filterAll.label") },
                { key: "active", label: t("AnomalyDetection.active.label") },
                { key: "inactive", label: t("AnomalyDetection.inactive.label") },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={listFilter === key}
                  className={`rule-accordion-filter${listFilter === key ? " is-active" : ""}`}
                  onClick={() => setListFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <ul className="rule-accordion-list" aria-label={t("AnomalyRuleConfig.ruleList.label")}>
            {filteredRules.length === 0 ? (
              <li className="rule-accordion-empty">{t("Nodatafound.label")}</li>
            ) : (
              filteredRules.map((rule) => renderRuleRow(rule))
            )}
          </ul>
        </div>
      )}

      {confirmToggle.show && (
        <ConfirmationModal
          modalShow={confirmToggle.show}
          title={confirmToggle.enabled ? t("AnomalyDetection.enableRule.label") : t("AnomalyDetection.disableRule.label")}
          messageText={
            confirmToggle.enabled
              ? t("AnomalyDetection.enableRuleConfirm.label").replace("{rule}", resolveAnomalyRuleLabel(t, confirmToggle.rule?.ruleCode))
              : t("AnomalyDetection.disableRuleConfirm.label").replace("{rule}", resolveAnomalyRuleLabel(t, confirmToggle.rule?.ruleCode))
          }
          callbackModal={handleToggleConfirm}
          confirmBtn={t("Confirm.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}

      <Modal
        show={showDetectModal}
        onHide={() => !isDetectBusy && setShowDetectModal(false)}
        centered
        className="rule-edit-modal detect-modal anomaly-glass-modal"
      >
        <Modal.Header closeButton className="rule-edit-modal-header">
          <div>
            <Modal.Title as="h6" className="rule-modal-name chart-header-with-tip">
              {t("AnomalyDetection.detectModalTitle.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.detectModalDesc.label")} />
            </Modal.Title>
            <p className="rule-modal-desc">{t("AnomalyDetection.detectModalDesc.label")}</p>
          </div>
        </Modal.Header>
        <Modal.Body className="rule-edit-modal-body">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="rule-field-label label-with-tip">
                {t("AnomalyDetection.detectMonth.label")}
                <AnomalyInfoTip text={t("AnomalyDetection.detectMonthToolTip.label")} />
              </Form.Label>
              <DatePicker
                selected={detectMonth}
                onChange={(date) => {
                  onAnomalyPeriodChange(setDetectMonth)(date);
                  setDetectError("");
                }}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                showFullMonthYearPicker
                maxDate={getAnomalyMaxSelectableDate()}
                className="form-control"
                placeholderText={t("AnomalyDetection.detectMonthPlaceholder.label")}
              />
            </Form.Group>
            {detectError && <p className="detect-field-error">{detectError}</p>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetectModal(false)} disabled={isDetectBusy}>
            {t("Cancel.label")}
          </Button>
          <Button
            variant="primary"
            className="sbmBtn"
            onClick={handleDetectSubmit}
            disabled={isDetectBusy || !detectMonth || !regulationId}
          >
            {isStartingDetect
              ? <ScaleLoader color="#fff" height={14} width={2} />
              : t("AnomalyDetection.runDetection.label")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default withTranslation()(AnomalyRuleConfig);
